/**
 * 📄 LISTE DES FACTURES FOURNISSEURS LOGISTICS
 * =============================================
 * Composant pour afficher et gérer les factures fournisseurs d'un dossier
 *
 * Fonctionnalités:
 * - Liste toutes les factures fournisseurs d'un shipment spécifique
 * - Filtres: statut paiement, fournisseur, dates, recherche
 * - Actions: Voir détail, Supprimer
 * - Statistiques rapides (total facturé, payé, en attente)
 * - Auto-refresh après actions
 *
 * Pattern source:
 * - Inspiré de: InvoiceList.tsx
 * - Adapté pour: Factures fournisseurs avec support multi-dossiers
 *
 * @see src/gestion-logistics/hooks/useSupplierBills.ts
 * @see src/gestion-logistics/components/SupplierBillDetailModal.tsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  DatePicker,
  Row,
  Col,
  Tooltip,
  theme,
} from 'antd';
import {
  ArrowRightOutlined,
  SearchOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/fr';
import useSupplierBills from '../hooks/useSupplierBills';
import { SupplierBill } from '../types/invoice.types';
import SupplierBillDetailModal from './SupplierBillDetailModal';
import { useLogisticsPermissions } from '../hooks/useLogisticsPermissions';

import { ShipmentDataTable, ShipmentMetricStrip, ShipmentTabHeading } from "./ShipmentDetailLayout";

const { RangePicker } = DatePicker;
const { Option } = Select;

dayjs.extend(isBetween);
dayjs.locale('fr');


interface SupplierBillListProps {
  shipmentId: number | null;
  onOpenCosting?: () => void;
  onRefreshNeeded?: () => void; // Callback pour refresh Job Costing après modif
}

const SupplierBillList: React.FC<SupplierBillListProps> = ({
  shipmentId,
  onRefreshNeeded,
  onOpenCosting,
}) => {
  const {
    loading,
    supplierBills,
    fetchSupplierBillsByShipment,
    deleteSupplierBill,
  } = useSupplierBills();

  const permissions = useLogisticsPermissions();

  const { token } = theme.useToken();
  const [selectedBill, setSelectedBill] = useState<SupplierBill | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Filtres
  const [searchText, setSearchText] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Statistiques locales (calculées depuis les factures filtrées)
  const [statistics, setStatistics] = useState({
    total_billed: 0,
    total_paid: 0,
    total_outstanding: 0,
  });

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Charger les factures du dossier
  const loadSupplierBills = useCallback(async () => {
    if (!shipmentId) {
      return;
    }

    const billsList = await fetchSupplierBillsByShipment(shipmentId);

    // Calculer les statistiques
    const stats = {
      total_billed: billsList.reduce(
        (sum: number, bill: SupplierBill) => sum + (bill.total_amount || 0),
        0
      ),
      total_paid: billsList.reduce(
        (sum: number, bill: SupplierBill) => sum + (bill.paid_amount || 0),
        0
      ),
      total_outstanding: billsList.reduce(
        (sum: number, bill: SupplierBill) => sum + (bill.balance_due || 0),
        0
      ),
    };
    setStatistics(stats);
  }, [shipmentId, fetchSupplierBillsByShipment]);

  useEffect(() => {
    loadSupplierBills();
  }, [loadSupplierBills]);

  // Voir détail d'une facture
  const handleViewDetail = (bill: SupplierBill) => {
    setSelectedBill(bill);
    setDetailModalVisible(true);
  };

  // Supprimer une facture
  const handleDelete = async (bill: SupplierBill) => {
    Modal.confirm({
      title: 'Supprimer la facture fournisseur',
      icon: <ExclamationCircleOutlined aria-hidden="true" />,
      content: (
        <>
          <p>
            Êtes-vous sûr de vouloir supprimer la facture{' '}
            <strong>{bill.bill_number}</strong> ?
          </p>
          <p style={{ color: token.colorErrorText, fontSize: '12px' }}>
            ⚠️ Les lignes de coût associées seront démarquées (is_supplier_billed
            → 0) et redeviendront disponibles pour facturation.
          </p>
        </>
      ),
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        const success = await deleteSupplierBill(bill.id);
        if (success) {
          loadSupplierBills();
          if (onRefreshNeeded) onRefreshNeeded();
        }
      },
    });
  };

  // Rendu du statut de paiement
  const renderPaymentStatus = (status: string) => {
    const statusConfig: Record<
      string,
      { color: string; icon: React.ReactNode; label: string }
    > = {
      paid: {
        color: 'success',
        icon: <CheckCircleOutlined aria-hidden="true" />,
        label: 'Payée',
      },
      partial: {
        color: 'warning',
        icon: <ClockCircleOutlined aria-hidden="true" />,
        label: 'Partielle',
      },
      unpaid: {
        color: 'default',
        icon: <ClockCircleOutlined aria-hidden="true" />,
        label: 'Non payée',
      },
      overdue: {
        color: 'error',
        icon: <ExclamationCircleOutlined aria-hidden="true" />,
        label: 'En retard',
      },
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  // Colonnes du tableau
  const columns: ColumnsType<SupplierBill> = [
    {
      title: 'N° Facture',
      dataIndex: 'bill_number',
      key: 'bill_number',
      width: 150,
      render: (billNumber: string, record: SupplierBill) => (
        <div>
          <div style={{ fontWeight: 500 }}>{billNumber}</div>
          {record.internal_reference && (
            <div style={{ fontSize: '11px', color: token.colorTextSecondary }}>
              Réf: {record.internal_reference}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Fournisseur',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
      render: (supplierName: string) => (
        <Space>
          <ShopOutlined style={{ color: "var(--color-primary)" }} />
          <span style={{ fontWeight: 500 }}>{supplierName}</span>
        </Space>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'bill_date',
      key: 'bill_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a: SupplierBill, b: SupplierBill) =>
        dayjs(a.bill_date).unix() - dayjs(b.bill_date).unix(),
    },
    {
      title: 'Dossiers',
      key: 'shipments',
      width: 100,
      align: 'center',
      render: (_, record: SupplierBill) => {
        if (record.is_multi_shipment) {
          return (
            <Tooltip
              title={`Couvre ${record.shipments_count} dossier(s) - ${record.total_lines_count} ligne(s)`}
            >
              <Tag color="warning" icon={<FileTextOutlined aria-hidden="true" />}>
                {record.shipments_count} dossiers
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Facture mono-dossier">
            <Tag color="default">1 dossier</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Montant total', key: 'amount', width: 150, align: 'right' as const,
      render: (_: unknown, record: SupplierBill) => <strong>{formatCurrency(record.total_amount)}</strong>,
      sorter: (a: SupplierBill, b: SupplierBill) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    { title: 'Payé', key: 'paid_amount', width: 140, align: 'right' as const,
      render: (_: unknown, record: SupplierBill) => <span style={{ color: token.green8 }}>{formatCurrency(record.paid_amount)}</span> },
    { title: 'Solde', key: 'balance_due', width: 140, align: 'right' as const,
      render: (_: unknown, record: SupplierBill) => <span style={{ color: token.orange8 }}>{formatCurrency(record.balance_due)}</span> },
    {
      title: 'Statut',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      align: 'center',
      render: (status: string) => renderPaymentStatus(status),
      filters: [
        { text: 'Payée', value: 'paid' },
        { text: 'Partielle', value: 'partial' },
        { text: 'Non payée', value: 'unpaid' },
        { text: 'En retard', value: 'overdue' },
      ],
      onFilter: (value: any, record: SupplierBill) =>
        record.payment_status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record: SupplierBill) => (
        <Space>
          <Tooltip title="Voir les détails">
            <Button
              type="link"
              aria-label={`Voir la facture ${record.bill_number}`} icon={<EyeOutlined aria-hidden="true" />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>

          {permissions.canCreateSupplierBills && (
            <Tooltip
              title={
                record.payment_status === 'paid'
                  ? 'Impossible de supprimer une facture payée'
                  : 'Supprimer la facture'
              }
            >
              <Button
                type="link"
                danger
                aria-label={`Supprimer la facture ${record.bill_number}`} icon={<DeleteOutlined aria-hidden="true" />}
                onClick={() => handleDelete(record)}
                disabled={record.payment_status === 'paid'}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Filtrer les données
  const filteredBills = supplierBills.filter((bill) => {
    // Filtre recherche
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchBillNumber = bill.bill_number.toLowerCase().includes(search);
      const matchSupplier = bill.supplier_name.toLowerCase().includes(search);
      const matchReference = bill.internal_reference?.toLowerCase().includes(search);
      if (!matchBillNumber && !matchSupplier && !matchReference) {
        return false;
      }
    }

    // Filtre statut paiement
    if (paymentStatusFilter !== 'all' && bill.payment_status !== paymentStatusFilter) {
      return false;
    }

    // Filtre date range
    if (dateRange) {
      const billDate = dayjs(bill.bill_date);
      if (!billDate.isBetween(dateRange[0], dateRange[1], 'day', '[]')) {
        return false;
      }
    }

    return true;
  });

  return (
    <div>
      <Card className="shipment-tab-panel">
        <ShipmentTabHeading title="Factures fournisseurs" icon={<FileTextOutlined aria-hidden="true" />}
          subtitle="Factures reçues et suivi des règlements" />
        <ShipmentMetricStrip items={[
          { label: "Total facturé", value: formatCurrency(statistics.total_billed || 0) },
          { label: "Total payé", value: formatCurrency(statistics.total_paid || 0), tone: "positive" },
          { label: "Solde restant", value: formatCurrency(statistics.total_outstanding || 0), tone: "warning" },
        ]} />
        <Card className="shipment-filter-panel">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={8}>
              <span className="shipment-filter-label">Recherche</span>
              <Input aria-label="Rechercher une facture fournisseur"
                placeholder="N° facture, fournisseur ou référence…"
                prefix={<SearchOutlined aria-hidden="true" />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <span className="shipment-filter-label">Statut de paiement</span>
              <Select aria-label="Statut de paiement" value={paymentStatusFilter} onChange={setPaymentStatusFilter} style={{ width: "100%" }}>
                <Option value="all">Tous les statuts</Option><Option value="unpaid">Non payée</Option>
                <Option value="partial">Partielle</Option><Option value="paid">Payée</Option><Option value="overdue">En retard</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <span className="shipment-filter-label">Période</span>
              <RangePicker aria-label="Période des factures" value={dateRange} onChange={dates => setDateRange(dates as [Dayjs, Dayjs] | null)}
                format="DD/MM/YYYY" placeholder={["Date début", "Date fin"]} style={{ width: "100%" }} />
            </Col>
            <Col xs={24} md={3}><Button icon={<ReloadOutlined aria-hidden="true" />} onClick={loadSupplierBills} loading={loading} block>Actualiser</Button></Col>
          </Row>
        </Card>
        <div className="shipment-table-panel"><ShipmentDataTable columns={columns} rows={filteredBills} loading={loading}
          emptyText="Aucune facture fournisseur pour ce dossier" /></div>
        <footer className="shipment-tab-footer">
          <p>La saisie des factures fournisseurs se fait depuis le Job Costing.</p>
          {onOpenCosting && <Button type="link" onClick={onOpenCosting}>Ouvrir le Job Costing <ArrowRightOutlined aria-hidden="true" /></Button>}
          <p>La colonne Dossiers indique les factures partagées entre plusieurs dossiers.</p>
        </footer>
      </Card>

      {/* Modal détails */}
      <SupplierBillDetailModal
        visible={detailModalVisible}
        bill={selectedBill}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedBill(null);
        }}
      />
    </div>
  );
};

export default SupplierBillList;
